import { CursorMode, type PageMeta } from '@repo/api/web/models'

/**
 * Краулябельная пагинация листинга поверх курсорного API (Offset у бэкенда
 * нет): курсор следующей страницы (`meta.cursorAfter` — пара строк field/id)
 * проносится через URL `?page=N&afterField=X&afterId=Y`, и каждая страница
 * рендерится в SSR одним запросом к API. Краулер обходит каталог по цепочке
 * видимых `<a rel="next">`-ссылок; пользователям остаётся бесконечный скролл.
 *
 * `page` — косметический счётчик для `<title>` и canonical: если пользователь
 * догрузил скроллом несколько батчей, ссылка «дальше» ведёт после последнего
 * из них, и номер может не совпадать с «арифметическим» — для краулера цепочка
 * всё равно честная (он не скроллит и ходит строго по ссылкам).
 *
 * Все три параметра валидны только вместе: `?page=N` без курсора игнорируется
 * (контент первой страницы, canonical без параметров — дубль склеивается сам).
 */
export function useListingPagination(options?: {
  /**
   * Имена query-параметров, которые проносятся в ссылку следующей страницы
   * (например `tag` фильтра контентных лент); в canonical не попадают —
   * фильтрованные страницы канонизируются консервативно, без параметров
   */
  carryParams?: string[]
}) {
  const route = useRoute()

  const parsed = computed(() => {
    const page = Number.parseInt(String(route.query.page ?? ''), 10)
    const afterField = String(route.query.afterField ?? '')
    const afterId = String(route.query.afterId ?? '')

    return Number.isInteger(page) && page >= 2 && afterField && afterId
      ? { page, afterField, afterId }
      : undefined
  })

  /** Номер текущей страницы (1 — обычный листинг без параметров) */
  const page = computed(() => parsed.value?.page ?? 1)

  const isPaginated = computed(() => Boolean(parsed.value))

  /** Курсорная часть initialQuery для useCursorListing (undefined на первой странице) */
  const cursorQuery = computed(() =>
    parsed.value
      ? {
          CursorMode: CursorMode.AFTER,
          CursorField: parsed.value.afterField,
          CursorId: parsed.value.afterId,
        }
      : undefined,
  )

  /** href следующей страницы по мете последнего загруженного батча */
  function nextPageHref(meta?: PageMeta | null): string | undefined {
    const cursor = meta?.cursorAfter
    if (!meta?.hasAfter || !cursor?.field || !cursor?.id) return undefined

    const params = new URLSearchParams({
      page: String(page.value + 1),
      afterField: cursor.field,
      afterId: cursor.id,
    })
    for (const name of options?.carryParams ?? []) {
      const value = String(route.query[name] ?? '').trim()
      if (value) params.set(name, value)
    }

    return `${route.path}?${params.toString()}`
  }

  /**
   * Query-суффикс самодостаточного canonical пагинированной страницы
   * ('' на первой). Страница 2+ канонизирует сама себя, а не первую:
   * canonical на первую заставил бы поисковик игнорировать содержимое
   * глубоких страниц — т.е. ссылки на товары из них.
   */
  const canonicalQuerySuffix = computed(() => {
    if (!parsed.value) return ''

    const params = new URLSearchParams({
      page: String(parsed.value.page),
      afterField: parsed.value.afterField,
      afterId: parsed.value.afterId,
    })

    return `?${params.toString()}`
  })

  return { page, isPaginated, cursorQuery, nextPageHref, canonicalQuerySuffix }
}
